  <!-- Checklist Header -->
  <div class="glass-card border border-light border-1 rounded-lg mb-3 p-3 shadow">
    <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
      <p class="text-x fw-bold mb-0">Compliance Checklist</p>
      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span class="status-badge text-xs" :class="completion_percentage === 100 ? 'manually-verified' : 'pending'">
          {{ completion_percentage }}% Complete
        </span>
        <button
          v-if="completion_percentage === 100"
          class="btn btn-primary-dark btn-sm rounded text-xxs"
          :disabled="is_rerunning_checks"
          @click="re_run_checks"
        >
          <span v-if="is_rerunning_checks" class="spinner-border spinner-border-sm text-white me-1"></span>
          Re-run checks
        </button>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="progress-bar-container">
      <div class="progress-bar-fill" :style="progress_bar_style"></div>
    </div>
    <div class="text-xxs fw-bold mt-2 text-end">
      {{ answered_target_questions }}/{{ total_target_questions }} target questions answered
    </div>
  </div>

  <!-- Checklist Items -->
  <div class="mt-3">
    <div v-if="is_loading" class="text-center py-4">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">Loading...</span>
    </div>
    <p class="mt-2">Loading compliance checklist...</p>
  </div>

  <div v-else-if="checklist_items.length === 0" class="text-center py-4 bg-transparent">
    <p>No checklist items found.</p>
  </div>

  <div v-else v-for="(item, index) in checklist_items" :key="item.id" class="mb-2">
    <div class="card shadow border-0 rounded-lg p-3 overflow-hidden w-100">

      <!-- Section Header -->
      <div class="text-xs fw-bold border-bottom border-2 pb-2 mb-3">

        <!-- Top Row: Title and Status -->
        <div
          class="d-flex justify-content-between align-items-start gap-2 mb-3 w-100 overflow-hidden"
          role="button"
          @click="toggle_section(index)">

          <!-- Left: Title with icon -->
          <div class="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden">
            <span :class="item.is_completed ? 'text-success' : 'text-danger'" class="fs-4 flex-shrink-0">
              <i :class="item.is_completed ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
            </span>
            <span class="text-capitalize text-x fw-bold text-truncate">{{ item.title }}</span>
          </div>

            <!-- Right: Badges and chevron -->
            <div class="d-flex align-items-center gap-1 flex-shrink-0 flex-wrap justify-content-end w-auto w-50">
              <span
                class="status-badge text-xs"
                :class="String(item.check_mode).toLowerCase() === 'manual'
                  ? (is_manual_checked(item) ? 'manually-verified' : 'pending')
                  : 'manually-verified'"
              >
                {{ String(item.check_mode).toLowerCase() === 'manual'
                  ? (is_manual_checked(item) ? 'Manually Checked' : 'Manual Check')
                  : 'Auto Check' }}
              </span>
              <span class="status-badge text-xs" :class="item.is_completed ? 'manually-verified' : 'pending'">
                {{ item.is_completed ? 'Completed' : 'Pending' }}
              </span>
              <i :class="item.is_open ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="flex-shrink-0"></i>
            </div>
          </div>

          <!-- Requirements Status Section (always visible) -->
          <div v-if="item.requirement_statuses && item.requirement_statuses.length > 0" class="mt-3 pt-2 border-top">
            <div class="p-2 rounded bg-light">
              <div class="d-flex align-items-center gap-2 mb-2">
                <span class="text-xs text-dark">
                  {{ item.requirement_statuses.filter(r => r.is_met).length }} of {{ item.requirement_statuses.length }} requirements met
                </span>
              </div>
              <div class="d-flex flex-wrap gap-1">
                <span
                  v-for="(req_status, idx) in item.requirement_statuses.filter(r => r.is_met)"
                  :key="'met-' + idx"
                  class="badge border border-success text-success text-xxs fw-normal px-2 py-1"
                >
                  <i class="bi bi-check-circle-fill me-1"></i>
                  {{ req_status.type }}
                  <template v-if="req_status.verification_type && req_status.verification_type !== 'MANUAL'">
                    :{{ req_status.verification_type }}
                  </template>
                </span>
                <span
                  v-for="(req_status, idx) in item.requirement_statuses.filter(r => !r.is_met)"
                  :key="'unmet-' + idx"
                  class="badge border border-danger text-danger text-xxs fw-normal px-2 py-1"
                >
                  <i class="bi bi-x-circle-fill me-1"></i>
                  {{ req_status.type }}
                  <template v-if="req_status.verification_type && req_status.verification_type !== 'MANUAL'">
                    : {{ req_status.verification_type }}
                  </template>
                </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Target Questions -->
      <div v-if="item.is_open">
        <div class="mb-2 d-flex align-items-center">
          <span class="text-x text-danger me-1">*</span>
          <span class="text-xs">Required Questions :</span>
          <span class="text-xs text-muted ms-1">
            {{ get_answered_count(item.target_questions) }} / {{ get_applicable_questions(item.target_questions).length }}
          </span>
        </div>

        <div
          v-for="(question, q_index) in get_applicable_questions(item.target_questions)"
          :key="question.id"
          class="mb-2 ms-2">

          <div class="d-flex align-items-start w-100 overflow-hidden">
            <span
              :class="get_question_status_variant(item, question) === 'passed'
                ? 'text-success'
                : (get_question_status_variant(item, question) === 'failed' ? 'text-danger' : 'text-muted')"
              class="me-2 fs-6 flex-shrink-0"
            >
              <i :class="get_question_status_variant(item, question) === 'passed'
                ? 'bi bi-check-circle-fill'
                : (get_question_status_variant(item, question) === 'failed'
                  ? 'bi bi-x-circle-fill'
                  : 'bi bi-circle')">
              </i>
            </span>

            <div class="flex-grow-1 overflow-hidden">
                  <div class="d-flex align-items-start justify-content-between gap-2">
                    <label class="text-dark text-xs text-capitalize fw-7 mb-1 flex-grow-1 text-wrap">
                      {{ q_index + 1 }}. {{ question.text }}
                    </label>
                    <button
                      v-if="get_question_check_reasons(item, question).length"
                      class="btn btn-link p-0 text-info text-xxs flex-shrink-0"
                      @click.stop="toggle_question_reasons(item, question)"
                      title="Show pass/fail details"
                    >
                      <i class="bi" :class="is_question_reasons_open(item, question) ? 'bi-info-circle-fill' : 'bi-info-circle'"></i>
                    </button>
                  </div>

              <!-- Reasons Panel -->
                  <div
                    v-if="is_question_reasons_open(item, question)"
                    class="mt-1 mb-2 p-2 border rounded bg-light"
                  >
                    <div
                      v-for="(reason, reason_index) in get_question_check_reasons(item, question)"
                      :key="`reason-${question.id}-${reason_index}`"
                      class="text-xxs mb-1"
                      :class="reason.is_met ? 'text-success' : 'text-danger'"
                    >
                      <i class="bi me-1" :class="reason.is_met ? 'bi-check-circle-fill' : 'bi-x-circle-fill'"></i>
                      <strong>{{ reason.type }}:</strong> {{ reason.message }}
                    </div>
                  </div>

                  <!-- Answer display -->
                  <div v-if="question.is_answered">

                    <!-- FILE TYPE -->
                    <div
                      v-if="question.question_type === 'file'"
                      class="d-flex align-items-center justify-content-between flex-wrap mt-3 ms-2 gap-2"
                    >
                      <span class="text-xxs text-muted fw-6 text-truncate flex-grow-1 me-3">
                        {{ get_file_name_from_url(question.answer) }}
                      </span>
                      <div class="d-flex align-items-center gap-3 flex-shrink-0">
                        <a
                          href="#"
                          class="text-dark text-decoration-none"
                          @click.prevent.stop="preview_file(question.answer)"
                          title="Preview"
                        >
                          <i class="bi bi-eye"></i>
                        </a>
                      </div>
                    </div>

                    <!-- NON-FILE TYPES -->
                    <div v-else class="text-xxs text-muted fst-italic text-wrap text-break">
                      Answer: {{ format_answer(question.answer) }}
                    </div>

                  </div>

                  <div v-else class="text-xxs text-danger">
                    No answer provided
                </div>
              </div>
            </div>
          </div>

          <!-- Submit Evaluation Button -->
          <div v-if="can_manual_check_item(item)" class="mt-3 ms-2 d-flex gap-2 flex-wrap">
            <button
              class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
              :class="is_manual_checked(item) ? 'btn-outline-danger' : 'btn-outline-primary'"
              @click="submit_evaluation_result(item.manual_association_id || item.association_id, !is_manual_checked(item))"
            >
              {{ is_manual_checked(item) ? 'Manual Uncheck' : 'Manual Check' }}
            </button>
            <button
              v-if="show_mark_as_evaluated(item)"
              class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
              @click="submit_evaluation_result(item.association_id, true)"
            >
              Mark as Evaluated
            </button>
          </div>

          <div v-else-if="show_mark_as_evaluated(item)" class="mt-3 ms-2">
            <button
              v-if="!item.is_evaluated"
              class="btn btn-primary-dark btn-sm rounded text-xxs shadow"
              @click="submit_evaluation_result(item.association_id, true)"
            >
              Mark as Evaluated
            </button>
            <button
              v-else
              class="btn btn-success btn-sm rounded text-xxs shadow"
              disabled
            >
              Evaluated
            </button>
          </div>

      </div>
    </div>
  </div>
</div>

<!-- File Preview Modal -->
<div v-if="preview_modal" class="custom-modal-overlay" @click.self="preview_modal = false">
  <FilePreviewComponent
    :presigned_url="preview_url"
    :file_name="preview_file_name"
    @close="close_the_file_preview"
    :key="preview_url"
  />
</div>